import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Calendar, DollarSign, TrendingUp, Mountain } from 'lucide-react';

const LiveSummaryPanel = ({ journey, totalDays, totalCost, difficulty, choices }) => {
  const getDifficultyLevel = () => {
    if (difficulty <= 1) return { label: 'Easy', color: 'bg-green-500', width: '20%' };
    if (difficulty <= 2) return { label: 'Moderate', color: 'bg-blue-500', width: '40%' };
    if (difficulty <= 3) return { label: 'Challenging', color: 'bg-orange-500', width: '60%' };
    if (difficulty <= 4) return { label: 'Demanding', color: 'bg-red-500', width: '80%' };
    return { label: 'Extreme', color: 'bg-purple-500', width: '100%' };
  };

  const difficultyInfo = getDifficultyLevel();

  return (
    <div className="sticky top-24 space-y-4">
      {/* Main Summary Card */}
      <Card className="border-2 border-emerald-700 rounded-sm shadow-xl">
        <CardContent className="p-6">
          <div className="mb-6">
            <h3 className="text-2xl font-serif text-zinc-900 mb-2">
              Your {journey?.shortName}
            </h3>
            <p className="text-sm text-zinc-600">{journey?.region}</p>
          </div>

          <div className="space-y-4">
            {/* Duration */}
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-600">Total Duration</p>
                  <p className="text-2xl font-bold text-zinc-900">{totalDays} Days</p>
                </div>
              </div>
            </div>

            {/* Cost */}
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-600">Estimated Cost</p>
                  <p className="text-2xl font-bold text-zinc-900">${totalCost}</p>
                </div>
              </div>
            </div>

            {/* Difficulty */}
            <div className="p-4 bg-zinc-50 rounded-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Mountain className="w-5 h-5 text-red-700" />
                </div>
                <div>
                  <p className="text-sm text-zinc-600">Difficulty Level</p>
                  <p className="text-lg font-bold text-zinc-900">{difficultyInfo.label}</p>
                </div>
              </div>
              <div className="w-full h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${difficultyInfo.color} transition-all duration-500`}
                  style={{ width: difficultyInfo.width }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Choices Made */}
      {choices.length > 0 && (
        <Card className="border-2 border-zinc-200 rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-700" />
              <h4 className="font-semibold text-zinc-900">Your Choices</h4>
            </div>
            <div className="space-y-2">
              {choices.map((choice, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <span className="text-amber-600">✓</span>
                  <span className="text-zinc-600">{choice}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Updates Indicator */}
      <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span>Updates live as you choose</span>
      </div>
    </div>
  );
};

export default LiveSummaryPanel;